import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SupportRequestNudge } from '../support-system.models';

const MAX_NUDGES = 50;

/**
 * "Which nudges?": a search field over the target's nudges (by ID or name).
 * Picked nudges show as chips in the field; the list below stays open while
 * picking several and closes with Done, Escape or a click elsewhere.
 * Nothing but listed nudges can be picked.
 */
@Component({
  selector: 'app-request-nudge-picker',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './request-nudge-picker.component.html',
  styleUrls: ['./request-nudge-picker.component.css'],
})
export class RequestNudgePickerComponent {
  private host = inject(ElementRef<HTMLElement>);

  @Input() nudges: SupportRequestNudge[] = [];
  @Input() state: 'loading' | 'ready' | 'error' = 'loading';
  @Input() selected: string[] = [];
  /** "Optional" or "ID or name" next to the label. */
  @Input() hintKey = '';
  @Input() disabled = false;
  @Output() selectedChange = new EventEmitter<string[]>();
  @Output() retry = new EventEmitter<void>();

  @ViewChild('input') input?: ElementRef<HTMLInputElement>;

  open = false;
  query = '';
  /** Keyboard highlight in the list. */
  active = -1;

  get results(): SupportRequestNudge[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.nudges;
    return this.nudges.filter(
      (n) => n.id.toLowerCase().includes(q) || !!n.name?.toLowerCase().includes(q),
    );
  }

  isPicked(id: string): boolean {
    return this.selected.includes(id);
  }

  /** The first nudge's id, as the placeholder's example. */
  get example(): string {
    return this.nudges[0]?.id || '';
  }

  openList(): void {
    if (this.disabled || this.state !== 'ready') return;
    this.open = true;
  }

  /** Done: the leftover search text isn't a nudge, so it goes. */
  closeList(): void {
    this.open = false;
    this.query = '';
    this.active = -1;
  }

  onQuery(value: string): void {
    this.query = value;
    this.active = value.trim() && this.results.length ? 0 : -1;
    this.openList();
  }

  toggle(id: string): void {
    const ids = this.isPicked(id)
      ? this.selected.filter((n) => n !== id)
      : this.selected.length < MAX_NUDGES
        ? [...this.selected, id]
        : this.selected;
    this.selectedChange.emit(ids);
    this.input?.nativeElement.focus();
  }

  remove(id: string): void {
    this.selectedChange.emit(this.selected.filter((n) => n !== id));
    this.input?.nativeElement.focus();
  }

  onKeydown(event: KeyboardEvent): void {
    const results = this.results;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.openList();
        if (!results.length) return;
        this.active =
          event.key === 'ArrowDown'
            ? (this.active + 1) % results.length
            : (this.active - 1 + results.length) % results.length;
        this.scrollActiveIntoView();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.open && results[this.active]) this.toggle(results[this.active].id);
        break;
      case 'Escape':
        if (!this.open) return;
        // Closes the list, not the modal.
        event.preventDefault();
        event.stopPropagation();
        this.closeList();
        break;
      case 'Backspace':
        if (!this.query && this.selected.length) {
          this.selectedChange.emit(this.selected.slice(0, -1));
        }
        break;
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.closeList();
    }
  }

  /** Tabbing out of the field and the list closes it too. */
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (this.open && next && !this.host.nativeElement.contains(next)) this.closeList();
  }

  trackNudge(_: number, nudge: SupportRequestNudge): string {
    return nudge.id;
  }

  private scrollActiveIntoView(): void {
    setTimeout(() =>
      this.host.nativeElement
        .querySelector('.rnp-row--active')
        ?.scrollIntoView({ block: 'nearest' }),
    );
  }
}
